import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { DashboardFilters, WebSocketMessage } from '../services/types';

// UI State Interface
interface UIState {
  sidebarCollapsed: boolean;
  mobileMenuOpen: boolean;
  theme: 'light' | 'dark' | 'auto';
  notifications: NotificationState[];
}

interface NotificationState {
  id: string;
  type: 'success' | 'info' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: string;
  dismissed: boolean;
}

// Dashboard State Interface
interface DashboardState {
  filters: DashboardFilters;
  selectedTasks: string[];
  refreshInterval: number; // seconds
  autoRefreshEnabled: boolean;
}

// WebSocket State Interface
interface WebSocketState {
  connected: boolean;
  connectionAttempts: number;
  lastMessage?: WebSocketMessage;
  subscriptions: string[];
  systemPerformanceOverviewData: any;
  socketLoading: boolean;
}

// User Preferences Interface
interface UserPreferences {
  dateFormat: string;
  timezone: string;
  defaultPageSize: number;
  dashboardLayout: DashboardLayout;
}

interface DashboardLayout {
  cardLayout: 'grid' | 'list';
  chartType: 'line' | 'bar' | 'area';
  showAdvancedMetrics: boolean;
}

// Combined App State
interface AppState {
  // UI State
  ui: UIState;
  
  // Dashboard State
  dashboard: DashboardState;
  
  // WebSocket State
  websocket: WebSocketState;
  
  // User Preferences
  preferences: UserPreferences;
  
  // Actions
  toggleSidebar: () => void;
  setMobileMenuOpen: (open: boolean) => void;
  setTheme: (theme: 'light' | 'dark' | 'auto') => void;
  addNotification: (notification: Omit<NotificationState, 'id' | 'timestamp' | 'dismissed'>) => void;
  dismissNotification: (id: string) => void;
  clearNotifications: () => void;
  
  // Dashboard Actions
  setDashboardFilters: (filters: Partial<DashboardFilters>) => void;
  setSelectedTasks: (tasks: string[]) => void;
  toggleTaskSelection: (taskId: string) => void;
  setAutoRefresh: (enabled: boolean) => void;
  setRefreshInterval: (interval: number) => void;
  
  // WebSocket Actions
  setWebSocketConnected: (connected: boolean) => void;
  incrementConnectionAttempts: () => void;
  resetConnectionAttempts: () => void;
  setLastMessage: (message: WebSocketMessage) => void;
  setSubscriptions: (subscriptions: string[]) => void;
  setSystemPerformanceOverviewData: (data: any) => void;
  setSocketLoading: (loading: boolean) => void;
  
  // Preferences Actions
  setUserPreferences: (preferences: Partial<UserPreferences>) => void;
  setDashboardLayout: (layout: Partial<DashboardLayout>) => void;
}

// Default state values
const defaultDashboardFilters: DashboardFilters = {
  platforms: [],
  statuses: [],
  dateRange: {
    startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // Last 24 hours
    endDate: new Date().toISOString(),
    preset: 'last_24h',
  },
  searchQuery: '',
  sortBy: {
    field: 'create_time',
    direction: 'desc',
  },
  pageSize: 20,
  currentPage: 1,
};

const defaultPreferences: UserPreferences = {
  dateFormat: 'YYYY-MM-DD HH:mm:ss',
  timezone: 'UTC',
  defaultPageSize: 20,
  dashboardLayout: {
    cardLayout: 'grid',
    chartType: 'line',
    showAdvancedMetrics: false,
  },
};

// Create the store
export const useAppStore = create<AppState>()(
  devtools(
    (set) => ({
      // Initial state
      ui: {
        sidebarCollapsed: false,
        mobileMenuOpen: false,
        theme: 'light',
        notifications: [],
      },
      dashboard: {
        filters: defaultDashboardFilters,
        selectedTasks: [],
        refreshInterval: 30000, // 30 seconds
        autoRefreshEnabled: true,
      },
      websocket: {
        connected: false,
        connectionAttempts: 0,
        subscriptions: [],
        systemPerformanceOverviewData: {},
        socketLoading: false,
      },
      preferences: defaultPreferences,

      // UI Actions
      toggleSidebar: () =>
        set((state) => ({
          ui: {
            ...state.ui,
            sidebarCollapsed: !state.ui.sidebarCollapsed,
          },
        })),

      setMobileMenuOpen: (open) =>
        set((state) => ({
          ui: {
            ...state.ui,
            mobileMenuOpen: open,
          },
        })),

      setTheme: (theme) =>
        set((state) => ({
          ui: {
            ...state.ui,
            theme,
          },
        })),

      addNotification: (notification) =>
        set((state) => ({
          ui: {
            ...state.ui,
            notifications: [
              ...state.ui.notifications,
              {
                ...notification,
                id: `notification_${Date.now()}`,
                timestamp: new Date().toISOString(),
                dismissed: false,
              },
            ],
          },
        })),

      dismissNotification: (id) =>
        set((state) => ({
          ui: {
            ...state.ui,
            notifications: state.ui.notifications.map((n) =>
              n.id === id ? { ...n, dismissed: true } : n
            ),
          },
        })),

      clearNotifications: () =>
        set((state) => ({
          ui: {
            ...state.ui,
            notifications: [],
          },
        })),

      // Dashboard Actions
      setDashboardFilters: (filters) =>
        set((state) => ({
          dashboard: {
            ...state.dashboard,
            filters: {
              ...state.dashboard.filters,
              ...filters,
            },
          },
        })),

      setSelectedTasks: (tasks) =>
        set((state) => ({
          dashboard: {
            ...state.dashboard,
            selectedTasks: tasks,
          },
        })),

      toggleTaskSelection: (taskId) =>
        set((state) => {
          const { selectedTasks } = state.dashboard;
          const isSelected = selectedTasks.includes(taskId);
          
          return {
            dashboard: {
              ...state.dashboard,
              selectedTasks: isSelected
                ? selectedTasks.filter((id) => id !== taskId)
                : [...selectedTasks, taskId],
            },
          };
        }),

      setAutoRefresh: (enabled) =>
        set((state) => ({
          dashboard: {
            ...state.dashboard,
            autoRefreshEnabled: enabled,
          },
        })),

      setRefreshInterval: (interval) =>
        set((state) => ({
          dashboard: {
            ...state.dashboard,
            refreshInterval: interval,
          },
        })),

      // WebSocket Actions
      setWebSocketConnected: (connected) =>
        set((state) => ({
          websocket: {
            ...state.websocket,
            connected,
          },
        })),

      incrementConnectionAttempts: () =>
        set((state) => ({
          websocket: {
            ...state.websocket,
            connectionAttempts: state.websocket.connectionAttempts + 1,
          },
        })),

      resetConnectionAttempts: () =>
        set((state) => ({
          websocket: {
            ...state.websocket,
            connectionAttempts: 0,
          },
        })),

      setLastMessage: (message) =>
        set((state) => ({
          websocket: {
            ...state.websocket,
            lastMessage: message,
          },
        })),

      setSubscriptions: (subscriptions) =>
        set((state) => ({
          websocket: {
            ...state.websocket,
            subscriptions,
          },
        })),

      setSystemPerformanceOverviewData: (data) =>
        set((state) => ({
          websocket: {
            ...state.websocket,
            systemPerformanceOverviewData: data,
          },
        })),

      setSocketLoading: (loading) =>
        set((state) => ({
          websocket: {
            ...state.websocket,
            socketLoading: loading,
          },
        })),

      // Preferences Actions
      setUserPreferences: (preferences) =>
        set((state) => ({
          preferences: {
            ...state.preferences,
            ...preferences,
          },
        })),

      setDashboardLayout: (layout) =>
        set((state) => ({
          preferences: {
            ...state.preferences,
            dashboardLayout: {
              ...state.preferences.dashboardLayout,
              ...layout,
            },
          },
        })),
    }),
    {
      name: 'mds-dashboard-store',
      partialize: (state: any) => ({
        // Persist only UI preferences and dashboard settings
        ui: {
          sidebarCollapsed: state.ui.sidebarCollapsed,
          theme: state.ui.theme,
        },
        dashboard: {
          refreshInterval: state.dashboard.refreshInterval,
          autoRefreshEnabled: state.dashboard.autoRefreshEnabled,
        },
        preferences: state.preferences,
      }),
    }
  )
);

// Utility hooks for specific state slices
export const useUIState = () => useAppStore((state) => state.ui);
export const useDashboardState = () => useAppStore((state) => state.dashboard);
export const useWebSocketState = () => useAppStore((state) => state.websocket);
export const useUserPreferences = () => useAppStore((state) => state.preferences);

// Selector hooks for performance
export const useSidebarCollapsed = () => useAppStore((state) => state.ui.sidebarCollapsed);
export const useSelectedTasks = () => useAppStore((state) => state.dashboard.selectedTasks);
export const useWebSocketConnected = () => useAppStore((state) => state.websocket.connected);
export const useSystemPerformanceOverviewData = () => useAppStore((state) => state.websocket.systemPerformanceOverviewData);
export const useSocketLoading = () => useAppStore((state) => state.websocket.socketLoading);
export const useTheme = () => useAppStore((state) => state.ui.theme);